import { api, ApiError } from './api';

export interface PushKeyResponse {
  enabled: boolean;
  publicKey: string | null;
}

export interface PushStatus {
  enabled: boolean;
  devices: number;
  mine: number;
  deviceSubscribed: boolean;
}

export class PushError extends Error {
  code: string;

  constructor(code: string, message: string) {
    super(message);
    this.code = code;
  }
}

// Desativar em Configurações é uma escolha explícita: o pedido automático do
// painel não deve reativar por cima dela.
const OPT_OUT_KEY = 'nailsly.pushOptOut';

export function pushOptedOut(): boolean {
  try {
    return localStorage.getItem(OPT_OUT_KEY) === '1';
  } catch {
    return false;
  }
}

function setPushOptOut(value: boolean): void {
  try {
    if (value) localStorage.setItem(OPT_OUT_KEY, '1');
    else localStorage.removeItem(OPT_OUT_KEY);
  } catch {
    /* modo privado sem storage: segue sem a marca */
  }
}

export function pushSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

export function isIos(): boolean {
  const nav = navigator as Navigator & { standalone?: boolean };
  return /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) || nav.standalone === true;
}

export function isStandalone(): boolean {
  const nav = navigator as Navigator & { standalone?: boolean };
  return window.matchMedia?.('(display-mode: standalone)').matches || Boolean(nav.standalone);
}

async function registration(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null;
  const existing = await navigator.serviceWorker.getRegistration('/');
  if (!existing) return null;
  return navigator.serviceWorker.ready;
}

async function currentSubscription(): Promise<PushSubscription | null> {
  if (!pushSupported()) return null;
  const reg = await registration();
  if (!reg) return null;
  return reg.pushManager.getSubscription();
}

export async function getPushStatus(businessId: string): Promise<PushStatus> {
  const sub = await currentSubscription();
  return api.post<PushStatus>('/api/notifications/push/status', {
    businessId,
    ...(sub ? { endpoint: sub.endpoint } : {})
  });
}

export async function enablePush(businessId: string): Promise<PushStatus> {
  if (!pushSupported()) {
    throw new PushError('unsupported', 'Este navegador não suporta notificações push.');
  }
  if (isIos() && !isStandalone()) {
    throw new PushError('ios_install', 'No iPhone, adicione o AgendaPro à Tela de Início e ative as notificações por lá.');
  }
  const key = await api.get<PushKeyResponse>('/api/notifications/push/key');
  if (!key.enabled || !key.publicKey) {
    throw new PushError('not_configured', 'As notificações ainda não estão configuradas no servidor.');
  }
  if (Notification.permission === 'denied') {
    throw new PushError('permission_denied', 'As notificações estão bloqueadas para este site. Libere nas configurações do navegador.');
  }
  if (Notification.permission !== 'granted') {
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      throw new PushError('permission_denied', 'Permissão de notificações não concedida.');
    }
  }

  const reg = await registration();
  if (!reg) {
    throw new PushError('sw_unavailable', 'O app ainda não terminou de instalar neste aparelho. Recarregue a página e tente novamente.');
  }

  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(key.publicKey)
    });
  }
  const json = sub.toJSON();
  const p256dh = json.keys?.p256dh;
  const auth = json.keys?.auth;
  if (!p256dh || !auth) {
    await sub.unsubscribe().catch(() => undefined);
    throw new PushError('invalid_subscription', 'O navegador não devolveu a inscrição completa. Tente novamente.');
  }
  await api.post('/api/notifications/push/subscribe', {
    businessId,
    endpoint: sub.endpoint,
    keys: { p256dh, auth },
    userAgent: navigator.userAgent.slice(0, 300)
  });
  setPushOptOut(false);
  return getPushStatus(businessId);
}

export async function disablePush(businessId: string): Promise<PushStatus> {
  const sub = await currentSubscription();
  if (sub) {
    try {
      await api.del('/api/notifications/push/subscribe', { businessId, endpoint: sub.endpoint });
    } catch (err) {
      // Sem rede o servidor fica com a inscrição; ela expira sozinha quando o push service
      // responder 410. Ainda assim removemos o vínculo local.
      if (!(err instanceof ApiError)) throw err;
    }
    await sub.unsubscribe().catch(() => undefined);
  }
  setPushOptOut(true);
  return getPushStatus(businessId).catch(() => ({ enabled: false, devices: 0, mine: 0, deviceSubscribed: false }));
}

// No logout o cookie ainda vale: apaga a inscrição deste navegador para outra pessoa
// não receber avisos da empresa neste computador. Nunca lança.
export async function clearPushOnLogout(businessId: string | null): Promise<void> {
  try {
    const sub = await currentSubscription();
    if (!sub) return;
    if (businessId) await api.del('/api/notifications/push/subscribe', { businessId, endpoint: sub.endpoint });
    await sub.unsubscribe();
  } catch {
    /* segue o logout */
  }
}

export async function testPush(businessId: string): Promise<{ sent: number; failed: number }> {
  return api.post('/api/notifications/push/test', { businessId });
}

function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const normalized = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(normalized);
  const buffer = new ArrayBuffer(raw.length);
  const out = new Uint8Array(buffer);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}
